.wrapper {
  display: grid;
  min-width: 0;
}

.form {
  display: grid;
  min-width: 0;
}

.label {
  margin-bottom: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

/* Two controls side by side: the field carries the shell, the button stands on its own. */
.row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-sm);
}

/* The address is typed, so it is set in the code face. The button beside it is a label and not
   an address: it stays in the interface face, the one Hangul has a webfont for. */
.field {
  display: flex;
  flex: 1;
  align-items: center;
  min-height: 2.5rem;
  min-width: 0;
  padding-inline: var(--space-4);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
  font-family: var(--font-mono);
  transition:
    border-color var(--transition),
    box-shadow var(--transition);
}

.field:focus-within {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-subtle);
}

.prefix {
  flex: none;
  color: var(--color-text-faint);
  white-space: nowrap;
  user-select: none;
}

/* The field sets the height and the inset; the input has neither of its own, so what is typed
   continues the host as one address and the two ends of the field stay symmetric. */
.input {
  flex: 1;
  align-self: stretch;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: inherit;
}

.input:focus-visible {
  outline: none;
}

.input::placeholder {
  color: var(--color-text-faint);
}

/* The button stands beside the field, not in it. Its height and corners are a button's: both
   controls are sized by min-height, so neither can end up a border taller than the other. */
.submit {
  flex: none;
  padding-inline: var(--space-4);
}

/* The main control of the landing page: taller, and lit like the boxes further down the page so
   the eye lands on it first. The field and the button each carry the glow, so the pair reads as
   one lit control. */
.large {
  font-size: var(--text-md);
}

.large .submit {
  box-shadow: var(--glow-bright);
}

/* White, so the field reads as the thing to fill in. On white the dark text colours take over,
   and the focus ring has to be the accent itself: the subtle blue behind it would vanish. */
.large .field {
  border-color: transparent;
  background: var(--color-bg-invert);
  box-shadow: var(--glow-bright);
  color: var(--color-text-invert);
}

.large .prefix {
  color: var(--color-text-invert-muted);
}

.large .input::placeholder {
  color: var(--color-text-invert-faint);
}

/* On focus the ring goes on top of the glow, both in the one property. */
.large .field:focus-within {
  border-color: var(--color-accent-solid);
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--color-accent) 35%, transparent),
    var(--glow-bright);
}

.large .field,
.large .submit {
  min-height: 3rem;
}

.large .submit {
  padding-inline: var(--space-5);
  font-size: var(--text-md);
}

.largeLabel {
  margin-bottom: var(--space-3);
  color: var(--color-text);
  font-size: var(--text-md);
}

/* An error outranks the focus ring and the glow, so these rules come after every rule they have
   to beat: the control keeps saying something is wrong, whatever else is going on. */
.invalid .field {
  border-color: var(--color-danger);
  box-shadow: none;
}

.invalid .field:focus-within {
  border-color: var(--color-danger);
  box-shadow: 0 0 0 3px var(--color-danger-subtle);
}

/* The hint, or what went wrong, and the examples: one line of small text where there is room for
   one, and where there is not the examples drop whole to the next. Inline they read as one run,
   which is right under a wide field and a mess under a narrow one -- the sentence breaks in the
   middle and the examples start halfway along the line under it, a gap where no gap belongs. */
.footnote {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-5);
  margin-top: var(--space-2);
  padding-inline: var(--space-2);
  font-size: var(--text-xs);
  line-height: 1.6;
}

/* The sentence takes the room it needs and no more, so short prose leaves the examples beside
   it; what it cannot fit on one line it wraps on its own row, over even lines rather than a full
   one and a word. */
.hint,
.error {
  flex: 0 1 auto;
  text-wrap: balance;
}

.hint {
  color: var(--color-text-faint);
}

.error {
  color: var(--color-danger);
}

.examples {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-3);
}

.examplesLabel {
  color: var(--color-text-faint);
}

.exampleList {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
}

.example {
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  white-space: nowrap;
  text-decoration: none;
  transition: color var(--transition);
}

.example:hover {
  color: var(--color-accent);
  text-decoration: underline;
}

@media (max-width: 40rem) {
  /* The host takes the room the input needs on a narrow screen; the label already says GitHub. */
  .prefix {
    display: none;
  }

  .field {
    padding-inline: var(--space-3);
  }

  .large .submit {
    padding-inline: var(--space-4);
  }
}
